import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { initialSkills } from '../../data/initialData';
import ImageUpload from './ImageUpload';

const SkillsManager = () => {
  const [skills, setSkills] = useLocalStorage('skills', initialSkills);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    level: 50,
    category: 'Frontend',
    icon: '',
    color: 'from-cyan-400 to-blue-500'
  });

  const resetForm = () => {
    setFormData({
      name: '',
      level: 50,
      category: 'Frontend',
      icon: '',
      color: 'from-cyan-400 to-blue-500'
    });
    setIsAdding(false);
    setEditingId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const skillData = {
      ...formData,
      id: editingId || Date.now()
    };

    if (editingId) {
      setSkills(skills.map(s => s.id === editingId ? skillData : s));
    } else {
      setSkills([...skills, skillData]);
    }
    
    resetForm();
  };

  const handleEdit = (skill) => {
    setFormData({
      name: skill.name,
      level: skill.level,
      category: skill.category,
      icon: skill.icon || '',
      color: skill.color || 'from-cyan-400 to-blue-500'
    });
    setEditingId(skill.id);
    setIsAdding(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this skill?')) {
      setSkills(skills.filter(s => s.id !== id));
    }
  };

  const categories = ['Frontend', 'Backend', 'Database', 'Machine Learning', 'Natural Language Processing', 'DevOps', 'Cloud', 'Tools', 'Other'];
  
  const colorOptions = [
    'from-cyan-400 to-blue-500',
    'from-yellow-400 to-orange-500',
    'from-blue-500 to-indigo-600',
    'from-green-500 to-emerald-600',
    'from-green-600 to-lime-500',
    'from-green-500 to-emerald-700',
    'from-blue-600 to-indigo-700',
    'from-blue-400 to-cyan-500',
    'from-orange-500 to-yellow-600',
    'from-red-500 to-orange-600',
    'from-cyan-500 to-teal-600',
    'from-gray-800 to-black',
    'from-purple-500 to-pink-600',
    'from-pink-500 to-rose-600',
    'from-indigo-500 to-blue-600'
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Manage Skills</h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsAdding(true)}
          className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-lg"
        >
          Add Skill
        </motion.button>
      </div>

      {isAdding && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-700/50 rounded-lg p-6 mb-6 border border-slate-600"
        >
          <h3 className="text-xl font-semibold text-white mb-4">
            {editingId ? 'Edit Skill' : 'Add New Skill'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Skill Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-600/50 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-600/50 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Skill Level ({formData.level}%)
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={formData.level}
                onChange={(e) => setFormData({...formData, level: parseInt(e.target.value)})}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Beginner</span>
                <span>Expert</span>
              </div>
            </div>

            <ImageUpload
                currentImage={formData.icon}
                onImageChange={(image) => setFormData({...formData, icon: image})}
                label="Skill Icon (Upload technology logo)"
              />

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Color Gradient
              </label>
              <div className="grid grid-cols-3 gap-2">
                {colorOptions.map((color) => (
                  <motion.button
                    key={color}
                    type="button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setFormData({...formData, color})}
                    className={`h-10 rounded-lg border-2 transition-all ${
                      formData.color === color 
                        ? 'border-white scale-105' 
                        : 'border-transparent hover:border-slate-400'
                    } bg-gradient-to-r ${color}`}
                  />
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <motion.button
                type="submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                {editingId ? 'Update' : 'Add'} Skill
              </motion.button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={resetForm}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Cancel
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="space-y-4">
        {skills.map((skill, index) => (
          <motion.div
            key={skill.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-slate-700/30 rounded-lg p-4 border border-slate-600"
          >
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-2">
                  {skill.icon ? (
                    <div className="w-8 h-8 rounded-lg overflow-hidden border border-slate-600">
                      <img 
                        src={skill.icon} 
                        alt={skill.name}
                        className="w-full h-full object-contain bg-white"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold" style={{ display: skill.icon ? 'none' : 'flex' }}>
                        {skill.name.charAt(0).toUpperCase()}
                      </div>
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
                      {skill.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-white">{skill.name}</h3>
                    <span className="px-2 py-1 bg-cyan-600/30 text-cyan-300 text-xs rounded-full border border-cyan-600/50">
                      {skill.category}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <div className="bg-slate-600/50 rounded-full h-2 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${skill.level}%` }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className={`bg-gradient-to-r ${skill.color || 'from-cyan-500 to-blue-600'} h-full`}
                      />
                    </div>
                  </div>
                  <span className="text-cyan-400 font-medium text-sm w-12 text-right">
                    {skill.level}%
                  </span>
                </div>
              </div>
              <div className="flex space-x-2 ml-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleEdit(skill)}
                  className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors"
                >
                  ✏️
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleDelete(skill.id)}
                  className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                >
                  🗑️
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default SkillsManager;
